import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './pages.css';

const LOAN_TYPE_META = {
    'Personal Loan':           { icon: '👤', color: '#a5761e' },
    'Two-Wheeler Loan':        { icon: '🛵', color: '#0ea5e9' },
    'Three-Wheeler Loan':      { icon: '🚐', color: '#f59e0b' },
    'Loan Against Property':   { icon: '🏠', color: '#10b981' },
    'Business Loan':           { icon: '🏢', color: '#6366f1' },
    'Education Loan':          { icon: '🎓', color: '#ec4899' },
};

const getStatusBadge = (status) => {
    const map = {
        Active:   'pg-badge-green',
        Pending:  'pg-badge-amber',
        Approved: 'pg-badge-blue',
        Rejected: 'pg-badge-red',
        Closed:   'pg-badge-neutral',
    };
    return map[status] || 'pg-badge-neutral';
};

const getApprovalBadge = (s) => {
    if (s === 'Admin_Approved') return { cls: 'pg-badge-green',  label: '✓ Approved' };
    if (s === 'KYC_Approved')   return { cls: 'pg-badge-blue',   label: '🔍 Under Review' };
    if (s === 'Rejected')       return { cls: 'pg-badge-red',    label: '✗ Rejected' };
    return                             { cls: 'pg-badge-amber',  label: '⏳ Pending' };
};

const formatDate = (date) => date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
});

const getTenureDueDate = (loan) => {
    const tenureDays = Number(loan.tenure_months || 0);
    const isAwaitingDisbursal = !['Approved', 'Active'].includes(loan.status) &&
        !['Admin_Approved', 'Disbursed'].includes(loan.approval_status);
    const baseDateValue = isAwaitingDisbursal ? null : (loan.disbursement_date || loan.reviewed_at || loan.created_at);
    const baseDate = baseDateValue ? new Date(baseDateValue) : new Date();
    const savedDueDate = loan.due_date ? new Date(loan.due_date) : null;
    const hasSavedDueDate = savedDueDate && !Number.isNaN(savedDueDate.getTime());

    if (!Number.isFinite(tenureDays) || tenureDays <= 0 || Number.isNaN(baseDate.getTime())) {
        return hasSavedDueDate ? formatDate(savedDueDate) : 'N/A';
    }

    const dueDate = new Date(baseDate);
    // tenure_months is the interest-day count; on inclusive-interest loans
    // (both end days charged) the due date is one calendar day sooner.
    dueDate.setDate(dueDate.getDate() + tenureDays - (Number(loan.inclusive_interest_days) === 1 ? 1 : 0));
    const effectiveDueDate = hasSavedDueDate && savedDueDate > dueDate ? savedDueDate : dueDate;
    return formatDate(effectiveDueDate);
};

function LoansPage() {
    const navigate = useNavigate();
    const [loans, setLoans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [tab, setTab] = useState('all');
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchLoans = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (!session?.userId) return setIsLoading(false);
        setIsLoading(true);
        fetch(`${API_URL}/api/loans/${session.userId}`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    setLoans(data.loans.map(l => {
                        let computedProgress = l.progress || 0;
                        if (l.status === 'Pending') {
                            if (l.approval_status === 'Admin_Approved') computedProgress = 80;
                            else if (l.approval_status === 'KYC_Approved') computedProgress = 50;
                            else if (l.kyc_status === 'completed' || l.kyc_status === 'verified') computedProgress = 30;
                            else computedProgress = 10;
                        } else if (l.status === 'Closed') {
                            computedProgress = 100;
                        }
                        return {
                            ...l,
                            progress: computedProgress,
                            displayAmount: Number(l.amount).toLocaleString('en-IN'),
                            displayEmi:    Number(l.emi).toLocaleString('en-IN'),
                            dueDate: getTenureDueDate(l),
                            meta: LOAN_TYPE_META[l.type] || { icon: '💰', color: '#a5761e' },
                        };
                    }));
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [API_URL]);

    useEffect(() => { fetchLoans(); }, [fetchLoans]);

    const filtered = tab === 'all' ? loans : loans.filter(l => l.status.toLowerCase() === tab);

    const stats = {
        total: loans.length,
        active: loans.filter(l => l.status === 'Active').length,
        pending: loans.filter(l => l.status === 'Pending').length,
        // "Borrowed" = money actually disbursed. Pending / under-review / rejected loans
        // are not borrowed yet, so they must not inflate this total.
        totalAmt: loans
            .filter(l => ['Active', 'Closed'].includes(l.status) || l.approval_status === 'Disbursed')
            .reduce((s, l) => s + Number(l.amount || 0), 0),
    };

    if (isLoading) return (
        <div className="pg-loading"><div className="pg-spinner" /><span>Loading your loans…</span></div>
    );

    return (
        <div className="pg-root">
            {/* Header */}
            <div className="pg-header">
                <div className="pg-header-text">
                    <h1>💰 My Loans</h1>
                    <p>View and manage all your loan applications</p>
                </div>
                <div className="pg-header-actions">
                    <button className="pg-btn pg-btn-primary" onClick={() => navigate('/dashboard/apply-loan')}>
                        ✦ Apply for Loan
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="pg-stat-strip">
                <div className="pg-stat">
                    <span className="pg-stat-icon">📂</span>
                    <span className="pg-stat-label">Total Loans</span>
                    <span className="pg-stat-value">{stats.total}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">✅</span>
                    <span className="pg-stat-label">Active</span>
                    <span className="pg-stat-value">{stats.active}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">⏳</span>
                    <span className="pg-stat-label">Pending Review</span>
                    <span className="pg-stat-value">{stats.pending}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">💸</span>
                    <span className="pg-stat-label">Total Borrowed</span>
                    <span className="pg-stat-value" style={{ fontSize: '1.05rem' }}>₹{stats.totalAmt.toLocaleString('en-IN')}</span>
                </div>
            </div>

            {/* Loan Cards */}
            <div className="pg-panel">
                <div className="pg-panel-header">
                    <h2>Your Loans</h2>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {['all','active','pending','rejected'].map(t => (
                            <button key={t}
                                onClick={() => setTab(t)}
                                style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '999px',
                                    border: '1px solid',
                                    fontSize: '0.72rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    background: tab === t ? '#a5761e' : 'white',
                                    color: tab === t ? 'white' : '#64748b',
                                    borderColor: tab === t ? '#a5761e' : '#e2e8f0',
                                }}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {filtered.length === 0 ? (
                    <div className="pg-empty">
                        <span className="pg-empty-icon">💼</span>
                        <h3>No loans found</h3>
                        <p>You have no {tab !== 'all' ? tab : ''} loans yet. Apply for one to get started.</p>
                        <button className="pg-btn pg-btn-primary" style={{ marginTop: '0.75rem' }} onClick={() => navigate('/dashboard/apply-loan')}>
                            Apply for a Loan
                        </button>
                    </div>
                ) : (
                    <div className="pg-cards-grid">
                        {filtered.map(loan => {
                            const apv = getApprovalBadge(loan.approval_status);
                            const reviewStatus =
                                loan.status === 'Rejected' ? { label: '❌ Rejected', color: '#b91c1c' }
                                : (loan.status === 'Active' || loan.approval_status === 'Admin_Approved' || loan.approval_status === 'Disbursed') ? { label: '✅ Approved', color: '#15803d' }
                                : { label: '🕐 Under Review', color: '#b45309' };
                            return (
                                <div key={loan.id} className="pg-card">
                                    <div className="pg-card-accent" style={{ background: `linear-gradient(90deg, ${loan.meta.color}, #ddb25c)` }} />
                                    <div className="pg-card-header">
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                                                <span style={{ fontSize: '1.3rem' }}>{loan.meta.icon}</span>
                                                <p className="pg-card-title">{loan.type}</p>
                                            </div>
                                            <p className="pg-card-sub">{loan.account_number}</p>
                                        </div>
                                        <span className={`pg-badge ${getStatusBadge(loan.status)}`}>{loan.status}</span>
                                    </div>

                                    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: '0.65rem' }}>
                                        {/* <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Loan Amount</div>
                                            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1e1b4b', letterSpacing: '-0.5px' }}>₹{loan.displayAmount}</div>
                                        </div> */}

                                        <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Application Status</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: '800', color: reviewStatus.color, letterSpacing: '-0.3px' }}>
                                                {reviewStatus.label}
                                            </div>
                                        </div>

                                        {/* <div className="pg-info-grid">
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Total Repayment</span>
                                                <span className="pg-info-value">₹{loan.displayEmi}</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Next Due</span>
                                                <span className="pg-info-value">{loan.dueDate}</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Interest Rate</span>
                                                <span className="pg-info-value">{loan.interest_rate}% per day</span>
                                            </div>
                                            <div className="pg-info-item">
                                                <span className="pg-info-label">Tenure</span>
                                                <span className="pg-info-value">{loan.tenure_months} days</span>
                                            </div>
                                        </div> */}

                                        <div className="pg-progress-wrap">
                                            <div className="pg-progress-meta">
                                                <span>Loan Progress</span>
                                                <strong>{loan.progress}%</strong>
                                            </div>
                                            <div className="pg-progress-track">
                                                <div className="pg-progress-fill" style={{ width: `${loan.progress}%` }} />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span className={`pg-badge ${apv.cls}`}>{apv.label}</span>
                                            <button className="pg-btn pg-btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.78rem' }}
                                                onClick={() => { setSelectedLoan(loan); setShowDetails(true); }}>
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Loan Details Modal */}
            {showDetails && selectedLoan && (
                <div className="pg-modal-overlay" onClick={() => setShowDetails(false)}>
                    <div className="pg-modal pg-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <h2>{selectedLoan.meta.icon} {selectedLoan.type}</h2>
                            <button className="pg-modal-close" onClick={() => setShowDetails(false)}>×</button>
                        </div>
                        <div className="pg-modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem 1.5rem', marginBottom: '1.25rem' }}>
                                {[
                                    ['Account No.', selectedLoan.account_number],
                                    // ['Loan Amount', `₹${selectedLoan.displayAmount}`],
                                    // ['Total Repayment', `₹${selectedLoan.displayEmi}`],
                                    // ['Interest Rate', `${selectedLoan.interest_rate}% per day`],
                                    // ['Tenure', `${selectedLoan.tenure_months} days`],
                                    // ['Next Due Date', selectedLoan.dueDate],
                                    ['Application Status',
                                        selectedLoan.status === 'Rejected' ? '❌ Rejected'
                                        : (selectedLoan.status === 'Active' || selectedLoan.approval_status === 'Admin_Approved' || selectedLoan.approval_status === 'Disbursed') ? '✅ Approved'
                                        : '🕐 Under Review'],
                                    ['KYC Status', selectedLoan.kyc_status === 'completed' ? '✅ Verified' : '⏳ Pending'],
                                    ['Admin Approval', getApprovalBadge(selectedLoan.approval_status).label],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '0.68rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '0.15rem' }}>{label}</div>
                                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>{val}</div>
                                    </div>
                                ))}
                            </div>

                            {selectedLoan.rejection_reason && (
                                <div className="pg-alert pg-alert-danger" style={{ marginBottom: '1rem' }}>
                                    <span className="pg-alert-icon">⚠️</span>
                                    <div className="pg-alert-body">
                                        <h4>Rejection Reason</h4>
                                        <p>{selectedLoan.rejection_reason}</p>
                                    </div>
                                </div>
                            )}

                            <div className="pg-progress-wrap">
                                <div className="pg-progress-meta">
                                    <span>Loan Progress</span>
                                    <strong>{selectedLoan.progress}%</strong>
                                </div>
                                <div className="pg-progress-track" style={{ height: '12px' }}>
                                    <div className="pg-progress-fill" style={{ width: `${selectedLoan.progress}%` }} />
                                </div>
                            </div>
                        </div>
                        <div className="pg-modal-footer">
                            <button className="pg-btn pg-btn-ghost" onClick={() => setShowDetails(false)}>Close</button>
                            {selectedLoan.status === 'Active' && (
                                <button className="pg-btn pg-btn-primary" onClick={() => { setShowDetails(false); navigate('/dashboard/payments'); }}>
                                    💳 Make Payment
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoansPage;
